import { memo, useState } from 'react';
import './estilos.css';
import { BotonInfo } from './Modal';
import BotonColapsar from './BotonColapsar';
import MatrizTipologia from './MatrizTipologia';
import { txt } from '../i18n';
import { ETIQUETA_ALPHA } from '../alfas';

const NECESIDADES = [
  { clave: 'farmacias', color: '#ff5d73' },
  { clave: 'supermercados', color: '#40e0c8' },
  { clave: 'bancos', color: '#c792ea' },
  { clave: 'salud', color: '#4f93ff' },
].map((necesidad) => ({
  ...necesidad,
  etiqueta: txt(`necesidad.${necesidad.clave}`),
  corta: txt(`necesidad.${necesidad.clave}.corta`),
}));

const NECESIDAD_POR_CLAVE = Object.fromEntries(
  NECESIDADES.map((necesidad) => [necesidad.clave, necesidad]),
);


// orden explicito: Object.entries reordena '100' (clave entera canonica)
// antes que las claves con cero inicial
const ORDEN_ALFAS = ['000', '025', '050', '100'];

const COLOR_DESCONOCIDO = '#8a8a95';

function infoDeTipo(tipo) {
  return (
    NECESIDAD_POR_CLAVE[tipo] ?? {
      corta: tipo ? tipo.slice(0, 3).toUpperCase() : '???',
      color: COLOR_DESCONOCIDO,
    }
  );
}

function acotar(valor, minimo, maximo) {
  return Math.min(Math.max(valor, minimo), maximo);
}

function Panel({
  celda,
  esMovil,
  alpha,
  alcanzados,
  totalAlcanzables,
  t,
  mediaCiudad,
  destinosTrabajo,
  statsSegundoOrden,
  topDestinosTrabajo,
  tipologia,
  onInfo,
  onVerTipologia,
  mapaEnTipologia,
}) {
  // Pestana activa: analisis (puntuacion, tipologia, poblacion) o servicios
  // (encendidos y segundo orden). Dividido en dos para que el panel completo
  // quepa sin scroll.
  const [pestana, setPestana] = useState('analisis');
  // Plegado: deja solo el encabezado, para despejar el mapa sin perder de
  // vista que celda esta elegida
  const [colapsado, setColapsado] = useState(false);

  if (!celda) {
    return (
      <div className="hud-panel">
        <div className="hud-scanlines" />
        <div className="hud-contenido hud-contenido--espera">
          <p className="hud-espera">{esMovil ? txt('panel.esperaMovil') : txt('panel.espera')}</p>
        </div>
      </div>
    );
  }

  const rating = celda.ratings?.[alpha] ?? {};
  const total = acotar(rating.total ?? 0, 0, 1);
  const tSeguro = acotar(t, 0, 30);
  const completo = tSeguro >= 30;
  const encendidos = alcanzados.length;
  const ordenReciente = alcanzados.slice().reverse();
  const pesoMaximoDestino = topDestinosTrabajo?.[0]?.[1] ?? 1;

  // Version movil: una franja compacta con lo esencial (puntuacion,
  // tipologia y dos contadores). El analisis completo es de escritorio.
  if (esMovil) {
    return (
      <div className="hud-panel">
        <div className="hud-scanlines" />
        <div className="hud-contenido hud-contenido--movil">
          <div
            className="hud-progreso-riel"
            role="progressbar"
            aria-label={txt('panel.progresoAria')}
            aria-valuemin={0}
            aria-valuemax={30}
            aria-valuenow={Math.round(tSeguro)}
          >
            <div className="hud-progreso-relleno" style={{ width: `${(tSeguro / 30) * 100}%` }} />
          </div>
          <div className="hud-movil-fila">
            <div className="hud-movil-score">
              <span className="hud-seccion-titulo">E2SFCA (α = {ETIQUETA_ALPHA[alpha]})</span>
              <div className="hud-score">{total.toFixed(2)}</div>
            </div>
            <div className="hud-movil-tipo">
              {tipologia && (
                <span className="hud-tipologia-nombre" style={{ color: tipologia.color }}>
                  {tipologia.nombre}
                </span>
              )}
              <span className="hud-movil-dato">
                {encendidos}/{totalAlcanzables} {txt('panel.servicios')}
              </span>
              {statsSegundoOrden && (
                <span className="hud-movil-dato hud-movil-dato--rojo">
                  {statsSegundoOrden.nuevos} {txt('panel.soloDesdeTrabajoCorto')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hud-panel">
      <div className="hud-scanlines" />
      <div className="hud-contenido">
        <header className="hud-encabezado">
          <span className="hud-titulo">{txt('panel.sector')} {celda.h3.toUpperCase()}</span>
          <div className="hud-estado-fila">
            <span
              aria-live="polite"
              className={`hud-estado ${completo ? 'hud-estado--completo' : 'hud-estado--activo'}`}
            >
              {completo ? txt('panel.completo') : txt('panel.escaneando')}
            </span>
            <BotonInfo onClick={() => onInfo('escaneo')} etiqueta={txt('info.escaneo')} />
            <BotonColapsar
              colapsado={colapsado}
              onToggle={() => setColapsado((valor) => !valor)}
            />
          </div>
        </header>

        {!colapsado && (
          <>

            <div className="hud-progreso">
              <div
                className="hud-progreso-riel"
                role="progressbar"
                aria-label={txt('panel.progresoAria')}
                aria-valuemin={0}
                aria-valuemax={30}
                aria-valuenow={Math.round(tSeguro)}
              >
                <div className="hud-progreso-relleno" style={{ width: `${(tSeguro / 30) * 100}%` }} />
              </div>
              <span className="hud-progreso-etiqueta">
                {tSeguro.toFixed(1)} / 30 {txt('panel.min')}
              </span>
            </div>

            <div className="hud-pestanas" role="tablist" aria-label={txt('panel.tabsAria')}>
              <button
                type="button"
                role="tab"
                aria-selected={pestana === 'analisis'}
                className={`hud-pestana ${pestana === 'analisis' ? 'hud-pestana--activa' : ''}`}
                onClick={() => setPestana('analisis')}
              >
                {txt('panel.tabAnalisis')}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pestana === 'servicios'}
                className={`hud-pestana ${pestana === 'servicios' ? 'hud-pestana--activa' : ''}`}
                onClick={() => setPestana('servicios')}
              >
                {txt('panel.tabServicios')} {encendidos}/{totalAlcanzables}
              </button>
            </div>

            {pestana === 'analisis' && (
              <>
            <section className="hud-seccion">
              <div className="hud-seccion-cabecera">
                <span className="hud-seccion-titulo">
                  {txt('panel.puntuacion')} (α = {ETIQUETA_ALPHA[alpha]})
                </span>
                <BotonInfo onClick={() => onInfo('e2sfca')} etiqueta={txt('info.e2sfca')} />
              </div>
              <div className="hud-score">{total.toFixed(2)}</div>
              <ul className="hud-barras">
                {NECESIDADES.map(({ clave, etiqueta, color }) => {
                  const valor = acotar(rating[clave] ?? 0, 0, 1);
                  return (
                    <li key={clave} className="hud-barra-fila">
                      <span className="hud-barra-etiqueta">{etiqueta}</span>
                      <div className="hud-barra-riel">
                        <div
                          className="hud-barra-relleno"
                          style={{ width: `${valor * 100}%`, backgroundColor: color }}
                        />
                      </div>
                      <span className="hud-barra-valor">{valor.toFixed(2)}</span>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="hud-seccion">
              <span className="hud-seccion-titulo">{txt('panel.puntuacionPorAlfa')}</span>
              <ul className="hud-alfa-tabla">
                <li className="hud-alfa-fila hud-alfa-fila--cabecera">
                  <span />
                  <span>{txt('panel.celda')}</span>
                  <span>{txt('panel.ciudad')}</span>
                </li>
                {ORDEN_ALFAS.map((clave) => (
                  <li
                    key={clave}
                    className={`hud-alfa-fila ${clave === alpha ? 'hud-alfa-fila--activa' : ''}`}
                  >
                    <span>α = {ETIQUETA_ALPHA[clave]}</span>
                    <span>{acotar(celda.ratings?.[clave]?.total ?? 0, 0, 1).toFixed(2)}</span>
                    <span>{mediaCiudad?.[clave]?.toFixed(2) ?? ''}</span>
                  </li>
                ))}
              </ul>
            </section>

            {tipologia && (
              <section className="hud-seccion">
                <div className="hud-seccion-cabecera">
                  <span className="hud-seccion-titulo">{txt('panel.tipologia')}</span>
                  <BotonInfo onClick={() => onInfo('tipologia')} etiqueta={txt('info.tipologia')} />
                </div>
                <div className="hud-tipologia">
                  {/* Cuando el mapa ya esta coloreado por tipologia, la matriz de
                      la leyenda resalta esta misma celda: repetirla aqui seria
                      mostrar dos veces lo mismo */}
                  {!mapaEnTipologia && (
                    <div className="hud-tipologia-matriz">
                      <MatrizTipologia
                        accClass={tipologia.accClass}
                        pesoClass={tipologia.pesoClass}
                      />
                      <button type="button" className="hud-ver-mapa" onClick={onVerTipologia}>
                        {txt('panel.verEnMapa')}
                      </button>
                    </div>
                  )}
                  <div className="hud-tipologia-datos">
                    <span className="hud-tipologia-nombre" style={{ color: tipologia.color }}>
                      {tipologia.nombre}
                    </span>
                    <div className="hud-poblacion-fila">
                      <span>{txt('panel.retenida')}</span>
                      <span>{tipologia.retenida.toFixed(2)}</span>
                    </div>
                    <div className="hud-poblacion-fila">
                      <span>{txt('panel.peso')}</span>
                      <span>{(tipologia.peso * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section className="hud-seccion">
              <span className="hud-seccion-titulo">{txt('panel.poblacion')}</span>
              <div className="hud-poblacion-filas">
                <div className="hud-poblacion-fila">
                  <span>{txt('comun.residentes')}</span>
                  <span>{Math.round(celda.pop_vivienda ?? 0)}</span>
                </div>
                <div className="hud-poblacion-fila">
                  <span>{txt('comun.trabajadores')}</span>
                  <span>{Math.round(celda.pop_trabajo ?? 0)}</span>
                </div>
              </div>
            </section>
              </>
            )}

            {pestana === 'servicios' && (
              <>
            <section className="hud-seccion">
              <span className="hud-seccion-titulo">{txt('panel.serviciosEncendidos')}</span>
              <ul className="hud-lista-servicios">
                {ordenReciente.map((servicio) => {
                  const info = infoDeTipo(servicio.tipo);
                  return (
                    <li key={servicio.idx} className="hud-servicio-fila">
                      <span className="hud-servicio-tipo" style={{ backgroundColor: info.color }}>
                        {info.corta}
                      </span>
                      <span className="hud-servicio-nombre">{servicio.name}</span>
                      <span className="hud-servicio-minutos">{Math.round(servicio.minutos)}′</span>
                    </li>
                  );
                })}
                {encendidos === 0 && <li className="hud-servicio-vacio">{txt('panel.sinSenales')}</li>}
              </ul>
              <span className="hud-contador">
                {encendidos} / {totalAlcanzables} {txt('panel.serviciosAlcanzados')}
              </span>
            </section>

            <section className="hud-seccion hud-seccion--segundo-orden">
              <div className="hud-seccion-cabecera">
                <span className="hud-seccion-titulo">{txt('orden.segundo')}</span>
                <BotonInfo onClick={() => onInfo('segundoOrden')} etiqueta={txt('info.segundoOrden')} />
              </div>
              {destinosTrabajo ? (
                <>
                  <div className="hud-segundo-orden-resumen">
                    <span>{txt('panel.trabajadoresResidentes')}</span>
                    <span>{destinosTrabajo.trabajadores}</span>
                  </div>
                  <div className="hud-segundo-orden-resumen">
                    <span>{txt('panel.celdasTrabajo')}</span>
                    <span>{destinosTrabajo.n_destinos}</span>
                  </div>
                  {statsSegundoOrden && (
                    <>
                      <div className="hud-segundo-orden-resumen">
                        <span>{txt('panel.serviciosDesdeTrabajo')}</span>
                        <span>
                          {statsSegundoOrden.encendidos} / {statsSegundoOrden.total}
                        </span>
                      </div>
                      <div className="hud-segundo-orden-resumen hud-segundo-orden-resumen--nuevos">
                        <span>{txt('panel.soloDesdeTrabajo')}</span>
                        <span>{statsSegundoOrden.nuevos}</span>
                      </div>
                    </>
                  )}
                  {topDestinosTrabajo && topDestinosTrabajo.length > 0 && (
                    <ul className="hud-destinos-trabajo">
                      {topDestinosTrabajo.map(([workH3, n], indice) => (
                        <li key={workH3} className="hud-destino-fila">
                          <span className="hud-destino-etiqueta">
                            {txt('panel.destino')} {indice + 1}
                          </span>
                          <span className="hud-destino-valor">{n}</span>
                          <div className="hud-destino-riel">
                            <div
                              className="hud-destino-relleno"
                              style={{ width: `${(n / pesoMaximoDestino) * 100}%` }}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <span className="hud-sin-segundo-orden">{txt('panel.sinAsignacion')}</span>
              )}
            </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// El panel se re-renderiza al ritmo del reloj cuantizado (tPanel), no a los
// 60 fps de la animacion.
export default memo(Panel);
